import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from "react";

export type TransferKind = "upload-music" | "upload-image" | "upload-video" | "download" | "export";
export type TransferStatus = "running" | "done" | "error";

export interface TransferTask {
  id: string;
  label: string;
  kind: TransferKind;
  status: TransferStatus;
  progress: number;
  detail?: string;
  startedAt: number;
}

interface TransfersContextValue {
  tasks: TransferTask[];
  activeCount: number;
  start: (args: { label: string; kind: TransferKind; detail?: string; id?: string }) => string;
  update: (id: string, progress: number, detail?: string) => void;
  finish: (id: string, detail?: string) => void;
  fail: (id: string, detail?: string) => void;
  clearFinished: () => void;
}

const TransfersContext = createContext<TransfersContextValue | null>(null);

export const TransfersProvider = ({ children }: { children: ReactNode }) => {
  const [tasks, setTasks] = useState<TransferTask[]>([]);

  const start = useCallback(({ label, kind, detail, id }: { label: string; kind: TransferKind; detail?: string; id?: string }) => {
    const taskId = id ?? `${kind}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const task: TransferTask = { id: taskId, label, kind, detail, status: "running", progress: 0, startedAt: Date.now() };
    setTasks((prev) => [task, ...prev].slice(0, 30));
    return taskId;
  }, []);

  const patch = useCallback((id: string, next: Partial<TransferTask>) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, ...next } : t)));
  }, []);

  const value = useMemo<TransfersContextValue>(() => ({
    tasks,
    activeCount: tasks.filter((t) => t.status === "running").length,
    start,
    update: (id, progress, detail) => patch(id, { progress: Math.max(0, Math.min(100, progress)), detail }),
    finish: (id, detail) => patch(id, { status: "done", progress: 100, detail }),
    fail: (id, detail) => patch(id, { status: "error", detail }),
    clearFinished: () => setTasks((prev) => prev.filter((t) => t.status === "running")),
  }), [tasks, start, patch]);

  return <TransfersContext.Provider value={value}>{children}</TransfersContext.Provider>;
};

export function useTransfers() {
  const ctx = useContext(TransfersContext);
  if (!ctx) throw new Error("useTransfers must be used inside TransfersProvider");
  return ctx;
}
